import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '@strava/ui/Avatar';
import I18n from 'utils/I18n';
import CTA from '../CTA';
import { useActivityPublicContext } from '../../ActivityPublicContext';

import styles from './styles.scss';

function CommentBody({ body }) {
  const { track } = useActivityPublicContext();
  if (typeof body === 'string') {
    return body;
  }

  // The order of these tokens will never change, so
  // it's ok to use `index` as a key.
  const formattedTokens = body.map((token, index) => {
    const key = index;
    if (token.type === 'mention_token') {
      return (
        <a
          key={key}
          className="mentioned-athlete"
          href={token.path}
          onClick={() => {
            track({
              element: 'comment_mention',
              action: 'click'
            });
          }}
        >
          {token.text}
        </a>
      );
    }

    return <span key={key}>{token.text}</span>;
  });

  return <>{formattedTokens}</>;
}

CommentBody.propTypes = {
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired
};

const Comment = ({
  body,
  displayName,
  avatar,
  athleteLink,
  hideAthleteLink
}) => {
  const { track } = useActivityPublicContext();
  return (
    <div className={styles.comment}>
      <div className={styles.avatarWrapper}>
        <Avatar
          name={displayName}
          src={avatar}
          type="athlete"
          className={styles.avatar}
          onClick={() => {
            track({
              element: 'comment_profile_picture',
              action: 'click'
            });
          }}
        />
      </div>

      <div className={styles.commentWrapper}>
        <div className={styles.commentAuthor}>
          {hideAthleteLink ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              onClick={() => {
                track({
                  element: 'comment_profile',
                  action: 'click'
                });
              }}
            >
              {displayName}
            </div>
          ) : (
            <a
              href={athleteLink}
              onClick={() => {
                track({
                  element: 'comment_profile',
                  action: 'click'
                });
              }}
            >
              {displayName}
            </a>
          )}
        </div>
        <div className={styles.commentBody}>
          <CommentBody body={body} />
        </div>
      </div>
    </div>
  );
};

Comment.propTypes = {
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  displayName: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  athleteLink: PropTypes.string.isRequired,
  hideAthleteLink: PropTypes.bool
};

Comment.defaultProps = {
  hideAthleteLink: false
};

const Comments = ({ comments, totalCommentCount }) => {
  const { openSignUpModal, track } = useActivityPublicContext();

  const handleKudoCommentClick = () => {
    track({ element: 'sign_up_to_comment', action: 'click' })
    openSignUpModal({
      variant: 'kudos_comment',
      redirectUrl: window.location.href
    });
  };

  const handleSignupToCommentEmptyStateClick = () => {
    track({ element: 'sign_up_to_comment_empty', action: 'click' })
    openSignUpModal({
      variant: 'kudos_comment',
      redirectUrl: window.location.href
    });
  };

  return (
    <div className={styles.commentsContainer}>
      {totalCommentCount > 0 ? (
        <>
          <h3>
            {I18n.t('strava.activities.show_public.comments_v2', {
              count: totalCommentCount
            })}
          </h3>
          <ol className={styles.commentsList}>
            {comments.map((comment) => (
              <li key={comment.id}>
                <Comment
                  body={comment.body}
                  displayName={comment.displayName}
                  avatar={comment.avatar}
                  athleteLink={comment.athleteLink}
                  hideAthleteLink={comment.hideAthleteLink}
                />
              </li>
            ))}
          </ol>
          <CTA
            className={styles.signUpButton}
            onClick={handleKudoCommentClick}
            data-cy="sign-up-to-comment-cta"
          >
            {I18n.t('strava.activities.show_public.signup_to_comment')}
          </CTA>
        </>
      ) : (
        <div className={styles.empty}>
          <h3>{I18n.t('strava.activities.show_public.no_comments')}</h3>
          <CTA
            className={styles.signUpButton}
            onClick={handleSignupToCommentEmptyStateClick}
            data-cy="sign-up-to-comment-cta"
          >
            {I18n.t('strava.activities.show_public.signup_to_comment')}
          </CTA>
        </div>
      )}
    </div>
  );
};

Comments.propTypes = {
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      avatar: PropTypes.string,
      // The comment body could either be a string or array of objects.
      body: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.object)
      ]),
      displayName: PropTypes.string,
      hideAthleteLink: PropTypes.boolean,
      athleteLink: PropTypes.string
    })
  ).isRequired,
  totalCommentCount: PropTypes.number.isRequired
};

export default Comments;
